import React, { useState, useRef } from "react";
import { 
  Paper, Tabs, Tab, Box, Typography, Button, Stack, Table, TableBody, 
  TableCell, TableContainer, TableHead, TableRow, Chip, TablePagination,
  IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Tooltip
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CustomTabPanel from "components/atoms/CustomTabPanel"; 
import CustomRightDrawer from "components/atoms/CustomRightDrawer"; 
import ClinicalRecordForm from "components/molecules/patient/singlePatient/medicalRecords/ClinicalRecordForm";
import { useGetClinicalRecords, useDeleteClinicalRecord } from "store/hooks/react-query/query/useClinicalRecords";
import CustomLoader from "components/atoms/CustomLoader";

// --- Vitals Validation Helpers ---
const isVitalNormal = (type, value) => {
  if (value === undefined || value === null || value === "") return true; // Ignore empty values

  if (type === 'bp') {
    const parts = String(value).split('/');
    if (parts.length !== 2) return true; // Skip validation if format isn't strictly sys/dia
    const sys = parseFloat(parts[0]);
    const dia = parseFloat(parts[1]);
    if (isNaN(sys) || isNaN(dia)) return true;
    return (sys >= 100 && sys <= 139) && (dia >= 50 && dia <= 89);
  }

  const numVal = parseFloat(value);
  if (isNaN(numVal)) return true;

  switch (type) {
    case 'temp': return numVal >= 36.0 && numVal <= 37.4;
    case 'pulse': return numVal >= 60 && numVal <= 100;
    case 'resp': return numVal >= 12 && numVal <= 20;
    case 'spo2': return numVal >= 95 && numVal <= 100;
    case 'rbs': return numVal >= 70 && numVal <= 180;
    default: return true;
  }
};

const getVitalRangeText = (type) => {
  switch (type) {
    case 'bp': return "Normal: 100-139 / 50-89";
    case 'temp': return "Normal: 36.0-37.4 °C";
    case 'pulse': return "Normal: 60-100 bpm";
    case 'resp': return "Normal: 12-20 cpm";
    case 'spo2': return "Normal: 95-100 %";
    case 'rbs': return "Normal: 70-180";
    default: return "";
  }
};

// --- UPDATED: RecordsTable ---
function RecordsTable({ patientId, type, onEdit }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  
  // Fetch paginated data
  const { data, isLoading } = useGetClinicalRecords(patientId, type, page + 1, rowsPerPage);
  const { mutate: deleteRecord, isLoading: isDeleting } = useDeleteClinicalRecord();

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeleteClick = (id) => {
    setRecordToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (recordToDelete) {
      deleteRecord(
        { id: recordToDelete, patientId },
        { 
          onSuccess: () => {
            setDeleteDialogOpen(false);
            setRecordToDelete(null);
          }
        }
      );
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setRecordToDelete(null);
  };

  const renderVitalCell = (vitalType, value, suffix = "") => {
    if (value === undefined || value === null || value === "") return "-";
    const isNormal = isVitalNormal(vitalType, value);
    
    const content = (
      <Typography 
        variant="body2" 
        color={isNormal ? "textPrimary" : "error"} 
        fontWeight={isNormal ? "normal" : "bold"}
        sx={!isNormal ? { cursor: 'help', textDecoration: 'underline dotted' } : {}}
      >
        {value}{suffix}
      </Typography>
    );

    return isNormal ? content : (
      <Tooltip title={`Abnormal reading. ${getVitalRangeText(vitalType)}`} arrow placement="top">
        {content}
      </Tooltip>
    );
  };

  if (isLoading) return <CustomLoader />;
  
  const records = data?.data?.data || [];
  const totalCount = data?.data?.total || 0;

  if (!records.length && page === 0) return <Typography p={2} color="textSecondary">No records found.</Typography>;

  return (
    <Box>
      <TableContainer component={Box} sx={{ maxHeight: 400, overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead sx={{ bgcolor: "#f5f5f5" }}>
            <TableRow>
              {type === 'VITAL_SIGNS' ? (
                <>
                  <TableCell>Date</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell>BP</TableCell>
                  <TableCell>Temp</TableCell>
                  <TableCell>Pulse</TableCell>
                  <TableCell>Resp</TableCell>
                  <TableCell>SPO2</TableCell>
                  <TableCell>RBS</TableCell>
                </>
              ) : (
                <>
                  <TableCell>Date & Time</TableCell>
                  <TableCell>Details</TableCell>
                  {type === 'FLUID_BALANCE' && <TableCell align="right">Balance</TableCell>}
                </>
              )}
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {records.map((row) => {
              const d = new Date(row.date);
              return (
                <TableRow key={row._id} hover>
                  {type === 'VITAL_SIGNS' ? (
                    <>
                      <TableCell>{d.toLocaleDateString()}</TableCell>
                      <TableCell>{d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</TableCell>
                      <TableCell>{renderVitalCell('bp', row.details?.bp)}</TableCell>
                      <TableCell>{renderVitalCell('temp', row.details?.temp, " °C")}</TableCell>
                      <TableCell>{renderVitalCell('pulse', row.details?.pulse)}</TableCell>
                      <TableCell>{renderVitalCell('resp', row.details?.resp)}</TableCell>
                      <TableCell>{renderVitalCell('spo2', row.details?.spo2, "%")}</TableCell>
                      <TableCell>{renderVitalCell('rbs', row.details?.rbs)}</TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell>{d.toLocaleString()}</TableCell>
                      <TableCell>
                        {type === 'FLUID_BALANCE' && (
                          <Box>
                            <Typography variant="caption" display="block">Input: {row.details?.inputType} ({row.details?.inputVolume}ml)</Typography>
                            <Typography variant="caption" display="block">Output: {row.details?.outputType} ({row.details?.outputVolume}ml)</Typography>
                          </Box>
                        )}
                        {type === 'CLINICAL_NOTE' && (
                          <Box>
                            <Chip label={row.details?.role} size="small" color={row.details?.role === 'DOCTOR' ? 'primary' : 'secondary'} sx={{mr: 1}}/>
                            <Typography component="span" fontWeight="bold">{row.details?.authorName}</Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>{row.details?.note}</Typography>
                            <Stack direction="row" spacing={2} flexWrap="wrap">
                              {row.details?.oxygenTherapy && (
                                <Typography variant="caption" color="text.secondary">
                                  <strong>Oxygen:</strong> {row.details.oxygenTherapy}
                                </Typography>
                              )}
                              {row.details?.consumables && (
                                <Typography variant="caption" color="text.secondary">
                                  <strong>Consumables:</strong> {row.details.consumables}
                                </Typography>
                              )}
                            </Stack>
                          </Box>
                        )}
                        {type === 'MEDICATION' && (
                           <Box>
                             {row.details?.medications?.map((m, i) => (
                               <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
                                 • <strong>{m.medicationName}</strong> 
                                 {m.dose && ` - ${m.dose}`} 
                                 {m.frequency && ` (${m.frequency})`}
                               </Typography>
                             ))}
                           </Box>
                        )}
                      </TableCell>
                      {type === 'FLUID_BALANCE' && (
                        <TableCell align="right">
                          <Chip label={`${row.details?.balance || 0} ml`} color={(row.details?.balance || 0) < 0 ? "error" : "success"} size="small" />
                        </TableCell>
                      )}
                    </>
                  )}
                  {/* Actions Column Body */}
                  <TableCell align="right">
                    <Stack direction="row" justifyContent="flex-end" spacing={1}>
                      <IconButton size="small" color="primary" onClick={() => onEdit(row)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDeleteClick(row._id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Pagination Controls */}
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <Dialog 
        open={deleteDialogOpen} 
        onClose={cancelDelete}
        PaperProps={{
          sx: { borderRadius: 3, padding: 1, backdropFilter: "blur(10px)" }
        }}
      >
        <DialogTitle fontWeight="bold">Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to permanently delete this clinical record? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: 2 }}>
          <Button onClick={cancelDelete} color="inherit" disabled={isDeleting}>Cancel</Button>
          <Button 
            onClick={confirmDelete} 
            color="error" 
            variant="contained" 
            disableElevation
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Delete Record"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default function ClinicalDocumentation({ patientId }) {
  const [tabValue, setTabValue] = useState(0);
  const [selectedRecord, setSelectedRecord] = useState(null); 
  const drawerRef = useRef();

  const handleOpenDrawer = (record = null) => {
    setSelectedRecord(record);
    if (drawerRef.current) drawerRef.current.handleToggle();
  };

  const handleCloseDrawer = () => {
    setSelectedRecord(null);
    if (drawerRef.current) drawerRef.current.handleToggle();
  };

  return (
    <Paper sx={{ p: 2, minHeight: "50vh", borderRadius: 2, boxShadow: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" fontWeight="bold">Clinical Documentation</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDrawer()}>
          Add Entry
        </Button>
      </Stack>

      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} indicatorColor="primary" textColor="primary" variant="scrollable">
        <Tab label="Vital Signs" />
        <Tab label="Fluid Balance" />
        <Tab label="Clinical Notes" />
        <Tab label="Medication" />
      </Tabs>

      <Box sx={{ mt: 2 }}>
        <CustomTabPanel value={tabValue} index={0}>
          <RecordsTable patientId={patientId} type="VITAL_SIGNS" onEdit={handleOpenDrawer} />
        </CustomTabPanel>
        <CustomTabPanel value={tabValue} index={1}>
          <RecordsTable patientId={patientId} type="FLUID_BALANCE" onEdit={handleOpenDrawer} />
        </CustomTabPanel>
        <CustomTabPanel value={tabValue} index={2}>
          <RecordsTable patientId={patientId} type="CLINICAL_NOTE" onEdit={handleOpenDrawer} />
        </CustomTabPanel>
        <CustomTabPanel value={tabValue} index={3}>
          <RecordsTable patientId={patientId} type="MEDICATION" onEdit={handleOpenDrawer} />
        </CustomTabPanel>
      </Box>

      <CustomRightDrawer ref={drawerRef} title={selectedRecord ? "Edit Clinical Entry" : "New Clinical Entry"}>
        <ClinicalRecordForm 
          patientId={patientId} 
          initialData={selectedRecord} 
          onSuccess={handleCloseDrawer} 
        />
      </CustomRightDrawer>
    </Paper>
  );
}